import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Swal from "sweetalert2";
import { FiImage, FiAlertCircle } from "react-icons/fi";
import { FaGripVertical, FaEdit, FaTrash } from "react-icons/fa";
import { X } from "lucide-react";
import axiosInstance from "../../utils/axios";
import AdminSearchBar from "../../components/AdminSideComponents/AdminSearchBar";
import uploadImage from "../../utils/CloudinaryUpload";
import { uploadVideoToCloudinary } from "../../utils/cloudinary";
import toast from "react-hot-toast";

const SkeletonCard = (idx) => (
  <div key={idx} className="animate-pulse flex flex-col space-y-3">
    <div className="w-full h-56 md:h-64 bg-gray-300 rounded-lg" />
    <div className="w-3/4 h-4 bg-gray-300 rounded mx-auto" />
  </div>
);

const GalleryCard = ({
  item,
  onEdit,
  onDelete,
  onOpenModal,
  dragHandleProps,
}) => {
  const HAS_TITLE = Boolean(item.title);

  return (
    <div className="relative bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden group transition-all duration-300 h-[310px] flex flex-col">
      {/* TOP BAR */}
      <div className="px-4 py-2 flex items-center justify-between bg-white/80 backdrop-blur-sm z-20">
        <div
          className="flex items-center gap-2 text-sm text-gray-600"
          {...dragHandleProps}
        >
          <FaGripVertical className="cursor-grab" />
          <span className="text-xs">Seq {item.sequence ?? 0}</span>
        </div>
      </div>

      {/* MAIN PREVIEW */}
      <div
        className={`relative cursor-pointer flex-shrink-0 ${
          HAS_TITLE ? "h-[220px]" : "h-[260px]"
        }`} // adjust height dynamically
        onClick={() => onOpenModal(item)}
      >
        {item.category === "video" ? (
          <video
            src={item.fileUrl || item.preview}
            muted
            playsInline
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105 bg-black"
          />
        ) : (
          <img
            src={item.fileUrl || item.preview}
            alt={item.title}
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          />
        )}

        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/15 transition-all duration-300"></div>
      </div>

      {/* TITLE */}
      {HAS_TITLE && (
        <div className="px-4 py-2 text-center flex-shrink-0">
          <h3 className="text-sm font-semibold text-gray-700 truncate">
            {item.title}
          </h3>
        </div>
      )}

      {/* ACTION BUTTONS */}
      <div className="absolute bottom-3 right-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-30">
        <button
          onClick={() => onEdit(item)}
          title="Edit"
          className="p-2 bg-white rounded-full shadow-md hover:bg-gray-100 transition"
        >
          <FaEdit className="text-gray-700" />
        </button>

        <button
          onClick={() => onDelete(item)}
          title="Delete"
          className="p-2 bg-red-600 rounded-full shadow-md hover:bg-red-700 transition"
        >
          <FaTrash className="text-white" />
        </button>
      </div>
    </div>
  );
};

const EditModal = ({ item, onClose, onSaved }) => {
  const [title, setTitle] = useState(item?.title || "");
  const [sequence, setSequence] = useState(item?.sequence ?? 0);
  const [category, setCategory] = useState(item?.category || "image");
  const [preview, setPreview] = useState(item?.fileUrl || item?.preview || "");
  const [uploading, setUploading] = useState(false);
  const [uploadedUrl, setUploadedUrl] = useState(item?.fileUrl || null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    setTitle(item?.title || "");
    setSequence(item?.sequence ?? 0);
    setCategory(item?.category || "image");
    setPreview(item?.fileUrl || item?.preview || "");
    setUploadedUrl(item?.fileUrl || null);
  }, [item]);

  // lock body scroll when modal open
  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => (document.body.style.overflow = "");
  }, []);

  // handle immediate upload when a new file is selected
  const handleReplaceFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploading(true);

    try {
      let url = null;
      const isVideoCategory = category === "video";
      const mime = f.type || "";

      if (mime.startsWith("video") || isVideoCategory) {
        url = await uploadVideoToCloudinary(f);
      } else {
        url = await uploadImage(f);
      }

      try {
        if (preview && preview.startsWith("blob:"))
          URL.revokeObjectURL(preview);
      } catch (err) {}

      const newPreview = url;
      setPreview(newPreview);
      setUploadedUrl(url);

      toast.success("File uploaded (temp). Click Save to persist.");
    } catch (err) {
      console.error("Replace upload failed:", err);
      toast.error("Replace upload failed");
    } finally {
      setUploading(false);
      // reset file input
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSave = async () => {
    // Prepare payload
    const payload = {
      title: title || "",
      sequence: typeof sequence === "number" ? sequence : Number(sequence || 0),
      category: category || "image",
      fileUrl: uploadedUrl || preview || "", // uploadedUrl preferred
    };

    try {
      const resp = await axiosInstance.put(`/gallery/${item._id}`, payload);
      const updated = resp?.data?.data ?? resp?.data;
      toast.success("Saved");
      onSaved(updated);
      onClose();
    } catch (err) {
      console.error("Edit save failed:", err);
      toast.error("Save failed");
    }
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="bg-white w-full max-w-xl rounded-lg shadow-xl p-6 relative"
          initial={{ y: 20, opacity: 0, scale: 0.98 }}
          animate={{ y: 0, opacity: 1, scale: 1 }}
          exit={{ y: 20, opacity: 0, scale: 0.98 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute -top-10 right-0 text-white bg-black/50 hover:bg-black/70 p-3 rounded-full"
            title="Close"
          >
            <X size={20} />
          </button>

          <h3 className="text-lg font-semibold mb-4">Edit Gallery Item</h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Preview */}
            <div>
              <div className="w-full h-56 bg-gray-100 rounded-lg overflow-hidden flex items-center justify-center">
                {category === "video" ? (
                  <video
                    src={preview}
                    controls
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <img
                    src={preview}
                    alt={title}
                    className="w-full h-full object-cover"
                  />
                )}
              </div>

              <div className="mt-3 flex items-center gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*,video/*"
                  onChange={handleReplaceFile}
                  className="hidden"
                />
                <button
                  onClick={() =>
                    fileInputRef.current && fileInputRef.current.click()
                  }
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Replace File
                </button>
                {uploading && (
                  <div className="text-sm text-gray-600">Uploading...</div>
                )}
              </div>
            </div>

            {/* Fields */}
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Title
              </label>
              <input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="mt-1 w-full border px-3 py-2 rounded"
                placeholder="Title (optional)"
              />

              <label className="block text-sm font-medium text-gray-700 mt-3">
                Sequence
              </label>
              <input
                value={sequence}
                onChange={(e) => setSequence(Number(e.target.value))}
                type="number"
                min={0}
                className="mt-1 w-36 border px-3 py-2 rounded"
                placeholder="0"
              />

              <label className="block text-sm font-medium text-gray-700 mt-3">
                Category
              </label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value)}
                className="mt-1 w-full border px-3 py-2 rounded"
              >
                <option value="image">Image</option>
                <option value="video">Video</option>
                <option value="media">Media</option>
              </select>

            </div>
          </div>

          {/* Actions */}
          <div className="mt-6 flex items-center justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2 rounded border">
              Cancel
            </button>
            <button
              onClick={handleSave}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700"
            >
              Save Changes
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

const AdminGallery = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [filterDate, setFilterDate] = useState("");
  const [activeTab, setActiveTab] = useState("all");
  const [selectedItem, setSelectedItem] = useState(null); // for lightbox / view
  const [editingItem, setEditingItem] = useState(null);
  const dragIndexRef = React.useRef(null);

  const fetchAll = async () => {
    try {
      setLoading(true);
      setError("");
      const resp = await axiosInstance.get("/gallery/admin");
      const data = resp?.data?.data ?? resp?.data ?? [];
      setItems(
        Array.isArray(data)
          ? data.sort((a, b) => (a.sequence ?? 0) - (b.sequence ?? 0))
          : []
      );
    } catch (err) {
      console.error("Fetch gallery admin error:", err);
      setError(err?.message || "Failed to load gallery");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // Delete handler
  const handleDelete = async (item) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Delete "${
        item.title || "this item"
      }"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axiosInstance.delete(`/gallery/${item._id}`);
          setItems((p) => p.filter((x) => x._id !== item._id));
          toast.success("Deleted");
        } catch (err) {
          console.error("Delete failed", err);
          toast.error("Delete failed");
        }
      }
    });
  };

  const handleEdit = (item) => {
    setEditingItem(item);
  };

  const handleEditSaved = (updated) => {
    setItems((prev) =>
      prev.map((p) => (p._id === (updated._id || updated.id) ? updated : p))
    );
    setEditingItem(null);
  };

  // Lightbox open (view)
  const openModal = (item) => setSelectedItem(item);
  const closeModal = () => setSelectedItem(null);

  // Search handler
  const handleSearch = (q) => setSearchQuery(q || "");

  // DateFilter component (same as before)
  const DateFilter = () => (
    <div className="px-4 sm:px-6 gap-x-2 flex items-center">
      <div className="flex items-center space-x-2">
        <label className="text-sm font-medium text-gray-600">
          Filter by Date:
        </label>
        <input
          type="date"
          value={filterDate}
          onChange={(e) => setFilterDate(e.target.value)}
          className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 outline-none text-sm"
        />
      </div>
    </div>
  );

  // Drag handlers (unchanged)
  const onDragStart = (e, index) => {
    dragIndexRef.current = index;
    e.dataTransfer.effectAllowed = "move";
    try {
      e.dataTransfer.setData("text/plain", String(index));
    } catch (err) {}
  };
  const onDragOver = (e) => e.preventDefault();
  const onDrop = async (e, dropIndex) => {
    e.preventDefault();
    const fromIndex =
      dragIndexRef.current ?? Number(e.dataTransfer.getData("text/plain"));
    if (fromIndex === undefined || fromIndex === null) return;
    const toIndex = dropIndex;
    if (fromIndex === toIndex) return;

    const next = Array.from(items);
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);

    const patched = next.map((it, idx) => ({ ...it, sequence: idx + 1 }));
    setItems(patched);

    const changed = patched.filter((newItem) => {
      const oldItem = items.find((old) => old._id === newItem._id);
      const oldSequence = oldItem?.sequence ?? 0;
      return newItem.sequence !== oldSequence;
    });

    try {
      await Promise.all(
        changed.map((c) =>
          axiosInstance.put(`/gallery/${c._id}`, { sequence: c.sequence })
        )
      );
      toast.success("Order saved");
    } catch (err) {
      console.error("Failed to update sequence", err);
      toast.error("Failed to save order. Refreshing.");
      fetchAll();
    } finally {
      dragIndexRef.current = null;
    }
  };

  // Filtering logic (tab + search + date)
  const filteredItems = items.filter((it) => {
    if (activeTab !== "all" && it.category !== activeTab) return false;
    if (searchQuery) {
      if (
        !it.title?.toLowerCase().includes(searchQuery.toLowerCase()) &&
        !it.album?.toLowerCase().includes(searchQuery.toLowerCase())
      ) {
        return false;
      }
    }
    if (filterDate) {
      const itemDate = new Date(it.createdAt).toISOString().split("T")[0];
      if (itemDate !== filterDate) return false;
    }
    return true;
  });

  return (
    <div className="flex flex-col w-full h-screen">
      <AdminSearchBar
        title="Gallery Management"
        onAddNewClick={() => (window.location.href = "/admin-gallery-upload")}
        onSearchClick={handleSearch}
        leftComponent={<DateFilter />}
      />

      {/* Tabs */}
      <div className="px-6 mt-3">
        <div className="flex justify-center gap-3">
          {["all", "image", "video", "media"].map((t) => (
            <button
              key={t}
              onClick={() => setActiveTab(t)}
              className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                activeTab === t
                  ? "bg-primaryColor text-white shadow-sm"
                  : "bg-white border border-gray-200 text-gray-700"
              }`}
            >
              {t === "all" ? "All" : t.charAt(0).toUpperCase() + t.slice(1)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex-1 overflow-y-auto hide-scrollbar p-6">
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 py-6 md:py-10">
            {Array.from({ length: 8 }).map((_, i) => (
              <SkeletonCard key={i} />
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center h-64 text-red-500">
            <FiAlertCircle size={40} />
            <p className="mt-2 font-medium">{error}</p>
          </div>
        ) : filteredItems.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 text-gray-500">
            <FiImage size={40} />
            <p className="mt-2 font-medium">No items found</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 hide-scrollbar">
            {filteredItems.map((item, idx) => (
              <div
                key={item._id}
                draggable
                onDragStart={(e) =>
                  onDragStart(
                    e,
                    items.findIndex((it) => it._id === item._id)
                  )
                }
                onDragOver={onDragOver}
                onDrop={(e) => onDrop(e, idx)}
              >
                <GalleryCard
                  item={item}
                  onEdit={handleEdit}
                  onDelete={() => handleDelete(item)}
                  onOpenModal={openModal}
                  dragHandleProps={{}}
                />
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Lightbox modal for viewing */}
      <AnimatePresence>
        {selectedItem && (
          <motion.div
            className="fixed max-h-screen inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={closeModal}
          >
            <motion.div
              className="relative max-w-6xl w-full"
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.8, opacity: 0 }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={closeModal}
                className="absolute -top-12 right-0 text-white bg-black/50 hover:bg-black/70 p-3 rounded-full"
              >
                <X size={24} />
              </button>

              {selectedItem.category === "video" ? (
                <video
                  src={selectedItem.fileUrl}
                  controls
                  className="w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
                />
              ) : (
                <img
                  src={selectedItem.fileUrl}
                  alt={selectedItem.title}
                  className="w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
                />
              )}

              {selectedItem.title && (
                <div className="mt-2 text-center">
                  <h3 className="text-white text-lg md:text-xl font-semibold">
                    {selectedItem.title}
                  </h3>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit modal for editing item */}
      {editingItem && (
        <EditModal
          item={editingItem}
          onClose={() => setEditingItem(null)}
          onSaved={handleEditSaved}
        />
      )}
    </div>
  );
};

export default AdminGallery;

// import React, { useState, useEffect } from "react";
// import { motion, AnimatePresence } from "framer-motion";
// import { X } from "lucide-react";
// import Swal from "sweetalert2";
// import { useNavigate } from "react-router-dom";
// import { FiImage, FiAlertCircle } from "react-icons/fi";
// import axiosInstance from "../../utils/axios";
// import AdminSearchBar from "../../components/AdminSideComponents/AdminSearchBar";

// const useBodyScrollLock = (isLocked) => {
//   useEffect(() => {
//     if (isLocked) {
//       document.body.style.overflow = "hidden";
//     } else {
//       document.body.style.overflow = "";
//     }
//     return () => (document.body.style.overflow = "");
//   }, [isLocked]);
// };

// // Skeleton loader
// const SkeletonCard = () => (
//   <div className="animate-pulse flex flex-col space-y-3">
//     <div className="w-full h-56 md:h-64 bg-gray-300 rounded-lg" />
//     <div className="h-4 bg-gray-300 rounded w-3/4 mx-auto" />
//   </div>
// );

// // Gallery Grid Component
// export const GalleryGrid = ({ galleryItems, onDelete }) => {
//   const [selectedImg, setSelectedImg] = useState(null);
//   const [selectedTitle, setSelectedTitle] = useState("");
//   useBodyScrollLock(selectedImg);

//   const handleImageClick = (item) => {
//     setSelectedImg(item.image);
//     setSelectedTitle(item.title);
//   };

//   const closeModal = () => {
//     setSelectedImg(null);
//     setSelectedTitle("");
//   };

//   // Escape key to close modal
//   React.useEffect(() => {
//     const handleEscapeKey = (event) => {
//       if (event.key === "Escape") closeModal();
//     };
//     if (selectedImg) {
//       document.addEventListener("keydown", handleEscapeKey);
//       return () => document.removeEventListener("keydown", handleEscapeKey);
//     }
//   }, [selectedImg]);

//   const handleDelete = (item) => {
//     Swal.fire({
//       title: "Are you sure?",
//       text: `Do you really want to delete "${item.title || "this image"}"?`,
//       icon: "warning",
//       showCancelButton: true,
//       confirmButtonColor: "#d33",
//       cancelButtonColor: "#3085d6",
//       confirmButtonText: "Yes, delete it!",
//     }).then((result) => {
//       if (result.isConfirmed) {
//         onDelete(item._id);
//         Swal.fire("Deleted!", "The image has been deleted.", "success");
//       }
//     });
//   };

//   return (
//     <div>
//       <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 ">
//         {galleryItems.map((item, index) => (
//           <motion.div
//             key={item._id}
//             className="flex flex-col space-y-3"
//             initial={{ opacity: 0, y: 30 }}
//             whileInView={{ opacity: 1, y: 0 }}
//             transition={{ duration: 0.4, delay: index * 0.05 }}
//             viewport={{ once: true }}
//           >
//             {/* Image Card */}
//             <div className="overflow-hidden rounded-lg shadow-md cursor-pointer group bg-white hover:shadow-xl transition-all duration-300 relative">
//               <div
//                 className="relative overflow-hidden"
//                 onClick={() => handleImageClick(item)}
//               >
//                 <img
//                   src={item.image}
//                   alt={item.title}
//                   className="w-full h-56 md:h-64 object-cover group-hover:scale-105 transition-transform duration-500"
//                   loading="lazy"
//                 />
//                 <div className="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-all duration-300" />
//               </div>

//               {/* Delete Button */}
//               <button
//                 onClick={() => handleDelete(item)}
//                 className="absolute top-3 right-3 bg-red-500 text-white px-2 py-1 rounded-md text-xs opacity-0 group-hover:opacity-100 transition-opacity duration-300"
//               >
//                 Delete
//               </button>
//             </div>

//             {/* Title */}
//             {item.title && (
//               <motion.h3
//                 className="text-center text-[#333] font-semibold text-sm sm:text-base px-2 leading-tight"
//                 initial={{ opacity: 0 }}
//                 whileInView={{ opacity: 1 }}
//                 transition={{ duration: 0.3, delay: index * 0.02 + 0.2 }}
//                 viewport={{ once: true }}
//               >
//                 {item.title}
//               </motion.h3>
//             )}
//           </motion.div>
//         ))}
//       </div>

//       {/* Fullscreen Modal */}
//       <AnimatePresence>
//         {selectedImg && (
//           <motion.div
//             className="fixed max-h-screen inset-0 bg-black/90 backdrop-blur-sm flex items-center justify-center z-50 p-4"
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             exit={{ opacity: 0 }}
//             onClick={closeModal}
//           >
//             <motion.div
//               className="relative max-w-6xl w-full"
//               initial={{ scale: 0.8, opacity: 0 }}
//               animate={{ scale: 1, opacity: 1 }}
//               exit={{ scale: 0.8, opacity: 0 }}
//               transition={{ type: "spring", stiffness: 200, damping: 20 }}
//               onClick={(e) => e.stopPropagation()}
//             >
//               <button
//                 onClick={closeModal}
//                 className="absolute -top-12 right-0 text-white bg-black/50 hover:bg-black/70 p-3 rounded-full"
//               >
//                 <X size={24} />
//               </button>

//               <img
//                 src={selectedImg}
//                 alt={selectedTitle}
//                 className="w-full max-h-[80vh] object-contain rounded-lg shadow-2xl"
//               />

//               {selectedTitle && (
//                 <div className="mt-2 text-center">
//                   <h3 className="text-white text-lg md:text-xl font-semibold">
//                     {selectedTitle}
//                   </h3>
//                 </div>
//               )}
//             </motion.div>
//           </motion.div>
//         )}
//       </AnimatePresence>
//     </div>
//   );
// };

// // Main Page
// const AdminGallery = () => {
//   const navigate = useNavigate();
//   const [galleryItems, setGalleryItems] = useState([]);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [filterDate, setFilterDate] = useState("");
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState("");
//   const [filteredItems, setFilteredItems] = useState([]);

//   const fetchGallery = async () => {
//     try {
//       setLoading(true);
//       setError("");
//       const resp = await axiosInstance.get("/gallery");
//       setGalleryItems(resp?.data);
//     } catch (error) {
//       console.log("Error fetching gallery,", error);
//       setError(error?.message || "Couldn't load images");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchGallery();
//   }, []);

//   const handleDelete = async (_id) => {
//     try {
//       setError("");
//       await axiosInstance.delete(`/gallery/${_id}`);
//       setGalleryItems((prev) => prev.filter((item) => item._id !== _id));
//     } catch (error) {
//       console.log("Error deleting gallery item,", error);
//       setError(error?.message || "Image couldn't be deleted");
//     }
//   };

//   // Handle search from AdminSearchBar
//   const handleSearchChange = (query) => {
//     setSearchQuery(query);
//   };

//   // Handle add new button click
//   const handleAddNewClick = () => {
//     navigate("/admin-gallery-upload");
//   };

//   const clearFilters = () => {
//     setSearchQuery("");
//     setFilterDate("");
//   };

//   useEffect(() => {
//     setFilteredItems(
//       galleryItems.filter((item) => {
//         const matchesTitle = item.title
//           ?.toLowerCase()
//           .includes(searchQuery.toLowerCase());

//         let matchesDate = true;
//         if (filterDate) {
//           const itemDate = new Date(item.createdAt).toISOString().split("T")[0];
//           matchesDate = itemDate === filterDate;
//         }

//         return matchesTitle && matchesDate;
//       })
//     );
//   }, [searchQuery, filterDate, galleryItems]);

//   const DateFilter = () => {
//     return (
//       <>
//         <div className="px-4 sm:px-6 gap-x-2 flex items-center justify-between">
//           <div className="flex items-center space-x-2">
//             <label className="text-sm font-medium text-gray-600">
//               Filter by Date:
//             </label>
//             <input
//               type="date"
//               value={filterDate}
//               onChange={(e) => setFilterDate(e.target.value)}
//               className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-1 focus:ring-blue-500 outline-none text-sm"
//             />
//           </div>
//           {/* Clear Filters Button */}
//           {(searchQuery || filterDate) && (
//             <button
//               onClick={clearFilters}
//               className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition text-sm font-medium"
//             >
//               Clear Filters
//             </button>
//           )}
//         </div>
//       </>
//     );
//   };
//   return (
//     <div className="flex flex-col w-full h-screen ">
//       {/* Header with AdminSearchBar */}
//       <AdminSearchBar
//         title="Gallery Management"
//         onAddNewClick={handleAddNewClick}
//         onSearchClick={handleSearchChange}
//         leftComponent={<DateFilter />}
//       />
//       {/* Scrollable Container */}
//       <div className="flex-1 overflow-y-auto p-6">
//         {loading ? (
//           <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 py-6 md:py-10">
//             {Array.from({ length: 6 }).map((_, i) => (
//               <SkeletonCard key={i} />
//             ))}
//           </div>
//         ) : error ? (
//           <div className="flex flex-col items-center justify-center h-64 text-red-500">
//             <FiAlertCircle size={40} />
//             <p className="mt-2 font-medium">{error}</p>
//             <button
//               onClick={() => window.location.reload()}
//               className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg"
//             >
//               Retry
//             </button>
//           </div>
//         ) : filteredItems.length === 0 ? (
//           <div className="flex flex-col items-center justify-center h-64 text-gray-500">
//             <FiImage size={40} />
//             <p className="mt-2 font-medium">No images found</p>
//             {(searchQuery || filterDate) && (
//               <button
//                 onClick={clearFilters}
//                 className="mt-3 px-4 py-2 bg-indigo-600 text-white rounded-lg"
//               >
//                 Clear Filters
//               </button>
//             )}
//           </div>
//         ) : (
//           <GalleryGrid galleryItems={filteredItems} onDelete={handleDelete} />
//         )}
//       </div>
//     </div>
//   );
// };

// export default AdminGallery;
